import { EventIdentifier, type GetBalanceCurrencyDTO, StakingType } from '@capsicum/common';
import {
	type PartnerWithImageMap,
	useFanTokenStakingAnalytics,
	usePartners,
	useWalletsBalanceWrapper
} from '@capsicum/socios-core';
import { useMemo } from 'react';
import { match } from 'ts-pattern';

import { useParticipationRequirement } from './useParticipationRequirement';

type UseParticipationGuardProps = {
	partnerID: string;
	tokensRequired: number;
};

type CommonReturnType = {
	currency?: GetBalanceCurrencyDTO;
	isLoading: boolean;
	partner?: PartnerWithImageMap;
	sendFanTokenStakingEvent: () => void;
};

type AllowedReturnType = CommonReturnType & {
	isAllowed: true;
	participation: 'ALLOWED';
};

type RestrictedReturnType = CommonReturnType & {
	isAllowed: false;
	participation: 'BUY' | 'MODAL' | 'STAKE';
};

type UseParticipationGuardReturn = AllowedReturnType | RestrictedReturnType;

export const useParticipationGuard = ({
	partnerID,
	tokensRequired
}: UseParticipationGuardProps): UseParticipationGuardReturn => {
	const { arePartnersLoading, getPartnerBy } = usePartners();
	const { isLoading: isParticipationRequirementLoading, requirement } = useParticipationRequirement();

	const {
		getCurrencyBy,
		hasEnoughBalance,
		hasEnoughStaked,
		isLoading: areWalletsLoading
	} = useWalletsBalanceWrapper({ isStakable: true });

	const partner = useMemo(() => getPartnerBy(p => p.id === partnerID), [partnerID, getPartnerBy]);

	const partnerCurrencyID = partner?.currencyId;

	const currency = useMemo(
		() => getCurrencyBy(c => c.currency.id === partnerCurrencyID),
		[getCurrencyBy, partnerCurrencyID]
	);

	const participation = useMemo(() => {
		const currencyID = partnerCurrencyID || '';

		return match<
			{
				enoughOwnedTokens: boolean;
				enoughStakedTokens: boolean;
				requirement: typeof requirement;
			},
			UseParticipationGuardReturn['participation']
		>({
			enoughOwnedTokens: hasEnoughBalance(currencyID, tokensRequired),
			enoughStakedTokens: hasEnoughStaked(currencyID, tokensRequired),
			requirement
		})
			.with(
				{
					enoughOwnedTokens: false,
					requirement: 'STAKED'
				},
				() => 'BUY'
			)
			.with(
				{
					enoughStakedTokens: false,
					requirement: 'STAKED'
				},
				() => 'STAKE'
			)
			.with(
				{
					enoughOwnedTokens: false,
					requirement: 'OWNED'
				},
				() => 'MODAL'
			)
			.otherwise(() => 'ALLOWED');
	}, [requirement, tokensRequired, hasEnoughStaked, hasEnoughBalance, partnerCurrencyID]);

	const { sendFanTokenStakingEvent } = useFanTokenStakingAnalytics({
		amount: currency?.usableAmount,
		currencyId: partner?.currencyId,
		currentPage: 'typeform_page',
		eventIdentifier: EventIdentifier.TriggerClickFtStaking,
		partnerId: partner?.id,
		transactionType: StakingType.STAKE
	});

	const common = {
		currency,
		isLoading: areWalletsLoading || arePartnersLoading || isParticipationRequirementLoading,
		partner,
		sendFanTokenStakingEvent
	};

	return participation === 'ALLOWED'
		? {
				...common,
				isAllowed: true,
				participation: 'ALLOWED'
			}
		: {
				...common,
				isAllowed: false,
				participation
			};
};
